// Intent: "Czat z nami" — the client's line to the office/caretaker from the
// app (stage B of agent mode; Żaneta's "klient pisze zadanie z telefonu").
// Requires a LINKED account (the conversation is pinned to the CRM customer);
// guests and unlinked accounts get an explainer + the connect CTA instead of
// a dead end. Foreground-only polling: 5 s while focused, cleared on blur.

import { router, useFocusEffect, useLocalSearchParams } from 'expo-router';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { ChatMessage, pollChatApi, sendChatMessageApi, startChatApi } from '@/api/mobile-api';
import { ChatThread } from '@/components/chat-thread';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ScreenHeader } from '@/components/ui/screen-header';
import { useAuthStore } from '@/store/auth-store';
import { colors, spacing } from '@/theme/tokens';

const POLL_MS = 5000;

export default function ChatScreen() {
  const { t } = useTranslation();
  // Optional composer prefill (goal upsell "Zapytaj opiekuna") — user still sends.
  const { draft } = useLocalSearchParams<{ draft?: string }>();
  const account = useAuthStore((s) => s.account);
  const linked = account?.linkStatus === 'LINKED';

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [closed, setClosed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Incremental poll cursor — survives re-renders, reset when the screen remounts.
  const lastIdRef = useRef(0);

  const mergeMessages = useCallback((incoming: ChatMessage[]) => {
    if (!incoming.length) return;
    setMessages((prev) => {
      const known = new Set(prev.map((m) => m.id));
      const fresh = incoming.filter((m) => !known.has(m.id));
      if (!fresh.length) return prev;
      const next = [...prev, ...fresh];
      lastIdRef.current = Math.max(lastIdRef.current, ...next.map((m) => m.id));
      return next;
    });
  }, []);

  useFocusEffect(
    useCallback(() => {
      if (!linked) return undefined;
      let cancelled = false;
      (async () => {
        const res = await startChatApi();
        if (cancelled) return;
        if (res.ok && res.data) {
          mergeMessages(res.data.messages);
          setClosed(res.data.status === 'CLOSED');
        } else {
          setError(res.message ?? t('chat.loadError'));
        }
      })();
      const interval = setInterval(async () => {
        const res = await pollChatApi(lastIdRef.current);
        if (cancelled || !res.ok || !res.data) return;
        mergeMessages(res.data.messages);
        if (res.data.status === 'CLOSED') setClosed(true);
      }, POLL_MS);
      return () => {
        cancelled = true;
        clearInterval(interval);
      };
    }, [linked, mergeMessages, t]),
  );

  const onSend = async (body: string) => {
    const res = await sendChatMessageApi(body);
    if (res.ok && res.data) {
      mergeMessages([res.data]);
      setError(null);
    } else {
      setError(res.message ?? t('chat.sendError'));
    }
  };

  const connectTarget = account ? '/auth/connect' : '/auth/login';

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top']}>
      <View style={{ paddingHorizontal: spacing.screen, paddingTop: spacing.sm }}>
        <ScreenHeader title={t('chat.title')} />
      </View>

      {linked ? (
        <>
          {error ? (
            <View style={{ paddingHorizontal: spacing.screen }}>
              <AppText variant="bodyMd" color={colors.warning}>
                {error}
              </AppText>
            </View>
          ) : null}
          <ChatThread
            messages={messages}
            me="VISITOR"
            onSend={onSend}
            sendDisabled={closed}
            initialDraft={typeof draft === 'string' ? draft : undefined}
          />
          {closed ? (
            <AppText
              variant="bodyMd"
              color={colors.textSecondary}
              style={{ textAlign: 'center', padding: spacing.sm }}
            >
              {t('chat.closedInfo')}
            </AppText>
          ) : null}
        </>
      ) : (
        <View style={{ padding: spacing.screen, gap: spacing.md }}>
          <Card style={{ gap: spacing.sm, backgroundColor: colors.infoBg }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
              <Ionicons name="chatbubble-ellipses-outline" size={28} color={colors.primary} />
              <AppText variant="headlineSm" style={{ flex: 1 }}>
                {t('chat.linkRequiredTitle')}
              </AppText>
            </View>
            <AppText variant="bodyLg">{t('chat.linkRequiredBody')}</AppText>
            <PrimaryButton
              label={t('chat.linkCta')}
              onPress={() => router.push(connectTarget as never)}
            />
          </Card>
        </View>
      )}
    </SafeAreaView>
  );
}
